import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CircleAlert, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Bayrak arkasındaki yeni akış: hazırla -> önizle -> onayla -> kuyruğa al -> işleniyor -> tamamlandı/hata.
// Eski "Stoğa işle" penceresine dokunmaz.  Sunucu 404/false dönerse bu bileşen hiç gösterilmez.
const STEPS = ["Hazırla", "Onayla", "Kuyruğa al", "İşleniyor", "Tamamlandı"];
const STEP_INDEX = {
  not_prepared: 0, prepared: 1, awaiting_approval: 1, approved: 2, queued: 2,
  processing: 3, retrying: 3, completed: 4, failed: 4, cancelled: 4,
};
const CHOICES = [
  { key: "stock", label: "Stoğa işle", actions: ["stock_draft"] },
  { key: "payable", label: "Gider / borç oluştur", actions: ["payable_draft"] },
  { key: "both", label: "Her ikisini yap", actions: ["stock_draft", "payable_draft"] },
];
const ACTION_LABELS = { stock_draft: "Stok", payable_draft: "Borç" };
const POLL_MS = 4000;

function warehouseRows(data) {
  const rows = Array.isArray(data) ? data : Array.isArray(data?.warehouses) ? data.warehouses : Array.isArray(data?.items) ? data.items : [];
  return rows.map((w) => ({ id: String(w.id ?? w.warehouse_id ?? ""), name: String(w.name ?? w.attributes?.name ?? w.id ?? "") })).filter((w) => w.id);
}

function StepBar({ step }) {
  const current = STEP_INDEX[step] ?? 0;
  const failed = step === "failed";
  return (
    <ol className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {STEPS.map((label, i) => {
        const done = i < current || step === "completed";
        const active = i === current && step !== "completed";
        const Icon = failed && i === current ? CircleAlert : done ? CheckCircle2 : active ? Loader2 : Circle;
        const spin = active && !failed && (step === "processing" || step === "retrying" || step === "queued");
        return (
          <li key={label} className={`flex items-center gap-1.5 ${done ? "text-emerald-700" : active ? (failed ? "text-rose-700" : "text-foreground font-semibold") : "text-muted-foreground"}`}>
            <Icon className={`h-3.5 w-3.5 ${spin ? "animate-spin" : ""}`} />{label}
          </li>
        );
      })}
    </ol>
  );
}

export default function IncomingInvoiceFlowDialog({ open, onClose, invoice, provider, inventoryOwner, capabilities }) {
  const [choice, setChoice] = useState("payable");
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesSupported, setWarehousesSupported] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const invoiceId = String(invoice?.provider_invoice_id || invoice?.id || "");
  const actions = (CHOICES.find((c) => c.key === choice) || CHOICES[1]).actions;
  const needsWarehouse = actions.includes("stock_draft");
  const base = { provider, provider_invoice_id: invoiceId, warehouse_id: warehouseId || null };

  const refresh = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const res = await api.post("/incoming-drafts/flow-status", { provider, provider_invoice_id: invoiceId, warehouse_id: warehouseId || null });
      setStatus(res?.actions || null);
      setLoadError("");
    } catch (e) {
      setLoadError(e?.message || "Durum alınamadı");
    }
  }, [invoiceId, provider, warehouseId]);

  useEffect(() => {
    if (!open) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      // Depo listesi yalnız kaynağı bilinen sağlayıcılar için; tahmin yok.
      // Stok sahibi gelen fatura kaynağından DEĞİL, firma yapılandırmasından gelir (sunucu bildirir).
      const path = inventoryOwner === "bizimhesap" ? "/bizimhesap/warehouses" : inventoryOwner === "parasut" ? "/parasut/warehouses" : null;
      if (!path) { setWarehousesSupported(false); return; }
      try {
        const rows = warehouseRows(await api.get(path));
        if (!cancelled) { setWarehouses(rows); setWarehousesSupported(true); }
      } catch { if (!cancelled) setWarehousesSupported(false); }
    })();
    return () => { cancelled = true; };
  }, [open, inventoryOwner]);

  const call = useCallback(async (kind, action) => {
    const entry = status?.[action];
    if (!entry?.idempotency_key) return;
    setBusy(true); // aynı anda ikinci tıklama engellenir
    try {
      await api.post(`/incoming-drafts/${kind}`, {
        ...base, confirmation: kind, action, idempotency_key: entry.idempotency_key,
      });
      await refresh();
    } catch (e) {
      toast.error(e?.message || "İşlem tamamlanamadı");
    } finally { setBusy(false); }
  }, [base, refresh, status]);

  const prepare = useCallback(async () => {
    if (needsWarehouse && !warehouseId) { toast.error("Stok için depo seçmelisiniz"); return; }
    setBusy(true);
    try {
      await api.post("/incoming-drafts/prepare", {
        ...base, confirmation: "prepare",
        prepare_stock: actions.includes("stock_draft"), prepare_payable: actions.includes("payable_draft"),
      });
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Taslak hazırlanamadı");
    } finally { setBusy(false); }
  }, [actions, base, needsWarehouse, refresh, warehouseId]);

  const stockBlocked = needsWarehouse && !warehousesSupported;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Gelen fatura işlem akışı</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div role="radiogroup" aria-label="İşlem seçimi" className="grid gap-2 sm:grid-cols-3">
            {CHOICES.map((c) => (
              <button key={c.key} type="button" role="radio" aria-checked={choice === c.key}
                onClick={() => setChoice(c.key)}
                className={`rounded-md border px-3 py-2 text-left text-sm ${choice === c.key ? "border-foreground bg-muted/50 font-semibold" : "border-border"}`}>
                {c.label}
              </button>
            ))}
          </div>

          {needsWarehouse ? (
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Depo (zorunlu)</span>
              {warehousesSupported ? (
                <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                  <option value="">Depo seçin…</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              ) : (
                <span className="text-xs text-rose-700">
                  {inventoryOwner === "obihub"
                    ? "ObiHub depo listesi bu ekrana henüz bağlı değil; stok seçeneği şimdilik kullanılamaz (borç seçeneği çalışır)."
                    : "Bu stok sahibi için depo listesi desteklenmiyor; stok seçeneği kullanılamaz."}
                </span>
              )}
            </label>
          ) : null}

          {loadError ? <p className="text-xs text-rose-700">{loadError}</p> : null}

          <div className="space-y-3">
            {actions.map((action) => {
              const s = status?.[action];
              return (
                <div key={action} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-semibold">{ACTION_LABELS[action]}</span>
                    <span className="text-xs text-muted-foreground">{s?.message || "Henüz hazırlanmadı."}</span>
                  </div>
                  <StepBar step={s?.step || "not_prepared"} />
                  <div className="mt-3 flex gap-2">
                    {(!s || s.step === "not_prepared") ? (
                      <Button size="sm" disabled={busy || stockBlocked} onClick={prepare}>
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Hazırla
                      </Button>
                    ) : null}
                    {s?.can_approve && capabilities?.approve !== false ? <Button size="sm" disabled={busy} onClick={() => call("approve", action)}>Onayla</Button> : null}
                    {s?.can_enqueue && capabilities?.enqueue !== false ? <Button size="sm" disabled={busy} onClick={() => call("enqueue", action)}>Kuyruğa al</Button> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
